import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { loginSchema, type LoginInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { FloatingField } from '../../../components/FloatingField';
import { ApiError } from '../../../lib/api-client';
import { useLogin } from '../api/auth-api';
import { AuthError, AuthLayout, AuthNotice } from '../components/AuthLayout';
import { safeReturnPath } from '../return-url';
import { SESSION_ENDED_REASON } from '../session-ended';
import styles from './auth-forms.module.css';

/**
 * AUTH-01 — sign in.
 *
 * **There is no sign-up link.** Accounts exist only via invite, so offering one
 * would be offering a door that does not open.
 *
 * The return URL is honoured here: arriving at `/candidates/42` without a session
 * sends you here carrying `next`, and signing in puts you back on `/candidates/42`
 * rather than on a dashboard you did not ask for.
 */
export function LoginPage(): ReactNode {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { next?: string; reason?: string };
  const login = useLogin();

  // Show/hide is presentation, not data — it never leaves this screen, so it is
  // local state rather than another field on the form.
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    // Ticked by default: that is what the app already did before the box existed,
    // so the default is the behaviour nobody asked to change.
    defaultValues: { email: '', password: '', rememberMe: true },
    // 06-qol §4: inline validation on blur, not only on submit.
    mode: 'onBlur',
  });

  // 06-qol §7: autofocus the first field on a form that IS the primary task.
  useEffect(() => {
    form.setFocus('email');
  }, [form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const result = await login.mutateAsync(values).catch((error: unknown) => {
      applyFieldErrors(error, form.setError);
      return null;
    });
    if (!result) return;

    if (result.outcome === 'two_factor_required') {
      // Carried in the URL rather than in memory, because the real cookie is not
      // issued until the code is accepted and this screen is about to unmount.
      // A reload mid-challenge then falls back to the default rather than to a
      // choice the user did not make.
      await navigate({
        to: '/verify-code',
        search: { next: search.next, remember: values.rememberMe === false ? '0' : undefined },
      });
      return;
    }
    await navigate({ to: safeReturnPath(search.next) });
  });

  const failure = login.error;
  const isLocked = failure?.code === 'account_locked';

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use the email address your admin invited."
      footer={
        <p className={styles.note}>
          Trouble signing in? Ask your admin — every account here is created by invitation.
          Interview panellists use the link in their invite.
        </p>
      }
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        {failure && !hasFieldErrors(failure) && <AuthError message={failure.message} />}
        {!failure && search.reason === SESSION_ENDED_REASON && (
          // §L26 standard message, word for word.
          <AuthNotice tone="info">Your session ended. Sign in to continue where you left off.</AuthNotice>
        )}

        <FloatingField
          {...form.register('email')}
          label="Email"
          error={form.formState.errors.email?.message}
          isRequired
          type="email"
          autoComplete="username"
          inputMode="email"
          placeholder="you@nestgroup.com"
        />

        <FloatingField
          {...form.register('password')}
          label="Password"
          error={form.formState.errors.password?.message}
          isRequired
          type={isPasswordVisible ? 'text' : 'password'}
          autoComplete="current-password"
          action={
            <span className={styles.fieldAction}>
              <Button
                variant="quiet"
                size="sm"
                aria-pressed={isPasswordVisible}
                onClick={() => setIsPasswordVisible((visible) => !visible)}
              >
                {isPasswordVisible ? 'Hide' : 'Show'}
              </Button>
            </span>
          }
        />

        {/* The two decisions you make about this form without typing into it. */}
        <div className={styles.row}>
          <Controller
            control={form.control}
            name="rememberMe"
            render={({ field }) => (
              <Checkbox
                checked={field.value !== false}
                onChange={(checked) => field.onChange(checked)}
                label="Keep me signed in"
              />
            )}
          />
          <Link to="/forgot-password" className={styles.link}>
            Forgot your password?
          </Link>
        </div>

        <div className={styles.actions}>
          <Button type="submit" variant="solid" size="lg" block isPending={login.isPending}>
            Sign in
          </Button>
          {isLocked && (
            <Link to="/account-locked" className={`${styles.link} ${styles.center}`}>
              What does locked mean?
            </Link>
          )}
        </div>
      </form>
    </AuthLayout>
  );
}

/**
 * Server-side field errors, attached to their inputs. §L26: validation is inline,
 * next to the field — a message at the top of a form is a message about the form,
 * not about the box the person has to fix.
 */
export function applyFieldErrors(
  error: unknown,
  setError: (name: never, value: { type: string; message: string }) => void,
): void {
  if (!(error instanceof ApiError)) return;
  for (const [field, message] of Object.entries(error.fields)) {
    setError(field as never, { type: 'server', message });
  }
}

export function hasFieldErrors(error: unknown): boolean {
  return error instanceof ApiError && Object.keys(error.fields).length > 0;
}
